import type { Doc } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";

import { RunStatusBadge } from "@/components/run-status-badge";
import { DisabledReasonTooltip } from "@/components/ui/disabled-reason-tooltip";
import { cn, formatDocumentTypeLabel, type DocumentTypeLabelValue } from "@/lib/utils";

type RevisionProcessingStatus = "pending" | "processing" | "completed" | "failed";

interface DocumentTypeLabelProps {
  documentType?: DocumentTypeLabelValue;
  /** Document types found across a mixed revision's pages. */
  pageDocumentTypes?: DocumentTypeLabelValue[];
  classificationStatus?: RevisionProcessingStatus;
  /** Per-page title-block indexing; the type can still change while it runs. */
  pageIndexingStatus?: RevisionProcessingStatus;
  /** Denormalized revision index status; absent for types that are not indexed. */
  indexingStatus?: RevisionProcessingStatus;
  /** Recorded errors; the failing step's error shows on hover over the Failed badge. */
  classificationError?: string;
  indexingError?: string;
  /** Narrow table cells: stack the index badge under the type instead of beside it. */
  compact?: boolean;
  className?: string;
}

function isInFlight(status?: RevisionProcessingStatus) {
  return status === "pending" || status === "processing";
}

/** The label props read from a listed revision (`indexingError` comes from `listByProject`). */
export function revisionTypeLabelProps(
  revision:
    | (Pick<
        Doc<"projectDocumentRevisions">,
        | "effectiveDocumentType"
        | "pageDocumentTypes"
        | "classificationStatus"
        | "pageIndexingStatus"
        | "indexingStatus"
        | "classificationError"
      > & { indexingError?: string })
    | null
    | undefined
) {
  return {
    documentType: revision?.effectiveDocumentType,
    pageDocumentTypes: revision?.pageDocumentTypes,
    classificationStatus: revision?.classificationStatus,
    pageIndexingStatus: revision?.pageIndexingStatus,
    indexingStatus: revision?.indexingStatus,
    classificationError: revision?.classificationError,
    indexingError: revision?.indexingError,
  };
}

/** Classification (including a packet build) or indexing failed; both are retryable. */
export function isRevisionProcessingFailed(revision: {
  classificationStatus?: RevisionProcessingStatus;
  indexingStatus?: RevisionProcessingStatus;
}) {
  return revision.classificationStatus === "failed" || revision.indexingStatus === "failed";
}

/**
 * Displays a revision's document type, or a "Classifying" indicator while
 * classification is still pending or in progress. Mixed revisions list the
 * types of their pages. Once classified, a badge shows whether the document
 * has been indexed, or that classification or indexing failed.
 */
export function DocumentTypeLabel({
  documentType,
  pageDocumentTypes,
  classificationStatus,
  pageIndexingStatus,
  indexingStatus,
  classificationError,
  indexingError,
  compact = false,
  className,
}: DocumentTypeLabelProps) {
  if (isInFlight(classificationStatus)) {
    return (
      <span className={cn("inline-flex items-center gap-1.5 text-muted-foreground", className)}>
        <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden="true" />
        Classifying
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex",
        compact ? "max-w-full flex-col items-start gap-0.5" : "flex-wrap items-center gap-1.5",
        className
      )}
    >
      <span className={compact ? "max-w-full truncate" : undefined}>
        {formatDocumentTypeLabel(documentType)}
      </span>
      {documentType === "mixed" && pageDocumentTypes?.length ? (
        <span className="max-w-full truncate text-xs text-muted-foreground">
          {pageDocumentTypes.map(formatDocumentTypeLabel).join(", ")}
        </span>
      ) : null}
      {isInFlight(pageIndexingStatus) ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="size-3 shrink-0 animate-spin" aria-hidden="true" />
          Reading Sheets
        </span>
      ) : null}
      {isRevisionProcessingFailed({ classificationStatus, indexingStatus }) ? (
        <FailedBadge reason={classificationStatus === "failed" ? classificationError : indexingError} />
      ) : indexingStatus ? (
        <RunStatusBadge status={indexingStatus} />
      ) : null}
    </span>
  );
}

function FailedBadge({ reason }: { reason?: string }) {
  return (
    <DisabledReasonTooltip
      reason={reason}
      className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <RunStatusBadge status="failed" />
    </DisabledReasonTooltip>
  );
}
